import { Link, useParams } from "react-router-dom";
import { ArrowRight, XCircle, Lightbulb, FunctionSquare } from "lucide-react";
import { getCalculator, getPuArticle } from "../../data/technical";
import { CALC_WIDGETS } from "./CalcWidgets";
import { PageHero } from "../site/PageHero";
import { Seo } from "../site/Seo";
import { SectionLabel } from "../site/SectionLabel";
import { TechnicalDisclaimer } from "../site/TechnicalDisclaimer";
import { getAdvisory } from "../../data/advisory";
import NotFound from "../../pages/NotFound";

const Block = ({ title, children }) => (
  <section className="border-t border-brand-line pt-8">
    <h2 className="mb-4 font-heading text-xl font-bold tracking-tight text-brand-ink">{title}</h2>
    {children}
  </section>
);

export const CalculatorPage = () => {
  const { slug } = useParams();
  const c = getCalculator(slug);
  if (!c) return <NotFound />;
  const Widget = CALC_WIDGETS[slug];
  const advisory = getAdvisory(c.advisory);

  return (
    <>
      <Seo
        title={`${c.title} — MATRIVON Technical Hub`}
        description={c.short}
        path={`/technical-hub/calculators/${c.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: c.title,
          applicationCategory: "EngineeringApplication",
          operatingSystem: "Web",
          description: c.short,
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }}
      />
      <PageHero
        variant="dark"
        label="Engineering Calculators"
        title={c.title}
        intro={c.short}
        crumbs={[
          { label: "Technical Hub", to: "/technical-hub" },
          { label: "Calculators", to: "/technical-hub/calculators" },
          { label: c.title },
        ]}
      />

      <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-10">
            {Widget && <Widget />}

            <Block title="Formula">
              <div className="flex items-start gap-3 border border-brand-line bg-white p-5">
                <FunctionSquare className="mt-0.5 h-5 w-5 shrink-0 text-brand-blue" />
                <code className="font-mono text-sm leading-relaxed text-brand-ink">{c.formula}</code>
              </div>
            </Block>

            <Block title="Explanation">
              <p className="text-[17px] leading-relaxed text-brand-ink/85">{c.explanation}</p>
            </Block>

            <Block title="Worked Example">
              <div className="border-l-2 border-brand-red bg-white px-6 py-5 text-[16px] leading-relaxed text-brand-ink/90">
                {c.example}
              </div>
            </Block>

            <Block title="Meaning of Inputs">
              <dl className="divide-y divide-brand-line border border-brand-line bg-white">
                {c.inputsMeaning.map((m) => (
                  <div key={m.k} className="grid gap-1 p-4 sm:grid-cols-[220px_1fr]">
                    <dt className="font-mono text-sm font-medium text-brand-blue">{m.k}</dt>
                    <dd className="text-sm text-brand-muted">{m.v}</dd>
                  </div>
                ))}
              </dl>
            </Block>

            <Block title="Meaning of Results">
              <dl className="divide-y divide-brand-line border border-brand-line bg-white">
                {c.resultsMeaning.map((m) => (
                  <div key={m.k} className="grid gap-1 p-4 sm:grid-cols-[220px_1fr]">
                    <dt className="font-mono text-sm font-medium text-brand-blue">{m.k}</dt>
                    <dd className="text-sm text-brand-muted">{m.v}</dd>
                  </div>
                ))}
              </dl>
            </Block>

            <Block title="Common Mistakes">
              <ul className="space-y-2.5">
                {c.mistakes.map((m, i) => (
                  <li key={i} className="flex gap-3 text-[16px] leading-relaxed text-brand-ink/85">
                    <XCircle className="mt-1 h-4 w-4 shrink-0 text-brand-red" /> <span>{m}</span>
                  </li>
                ))}
              </ul>
            </Block>

            <Block title="Practical Notes">
              <ul className="space-y-2.5">
                {c.notes.map((m, i) => (
                  <li key={i} className="flex gap-3 text-[16px] leading-relaxed text-brand-ink/85">
                    <Lightbulb className="mt-1 h-4 w-4 shrink-0 text-brand-blue" /> <span>{m}</span>
                  </li>
                ))}
              </ul>
            </Block>

            <TechnicalDisclaimer />
          </div>

          <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-line bg-white p-6">
              <SectionLabel>Related Articles</SectionLabel>
              <ul className="space-y-3">
                {c.related.map((s) => {
                  const a = getPuArticle(s);
                  if (!a) return null;
                  return (
                    <li key={s}>
                      <Link
                        to={`/technical-hub/polyurethane/${a.slug}`}
                        className="group flex items-start gap-2 text-sm font-medium text-brand-ink hover:text-brand-blue"
                      >
                        <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                        {a.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {advisory && (
              <div className="border border-brand-blue/20 bg-brand-blue/5 p-6">
                <SectionLabel>Relevant Advisory</SectionLabel>
                <h3 className="font-heading text-lg font-bold text-brand-ink">{advisory.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-muted">{advisory.short}</p>
                <Link
                  to={`/advisory/${advisory.slug}`}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:gap-2.5"
                >
                  Explore this advisory <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            <div className="border border-brand-line bg-brand-ink p-6 text-white">
              <h3 className="font-heading text-lg font-bold">More Calculators</h3>
              <p className="mt-2 text-sm text-white/60">Explore the full PU calculator suite.</p>
              <Link
                to="/technical-hub/calculators"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:gap-2.5"
              >
                View all calculators <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
};
